import { __decorate } from "tslib";
import { ArrayDecoder, AutoEncoder, BooleanDecoder, EnumDecoder, field, IntegerDecoder, NumberDecoder } from '@simonbackx/simple-encoding';
import { isSimpleError, isSimpleErrors, SimpleError } from '@simonbackx/simple-errors';
import { Formatter } from '@stamhoofd/utility';
import { ValidatedAddress } from '../addresses/Address';
import { ChoicesFilterChoice, ChoicesFilterDefinition, ChoicesFilterMode } from '../filters/ChoicesFilter';
import { RecordAnswerDecoder } from '../members/records/RecordAnswer';
import { RecordCategory } from '../members/records/RecordCategory';
import { PaymentMethod } from '../PaymentMethod';
import { Cart } from './Cart';
import { Customer } from './Customer';
import { Discount } from './Discount';
import { DiscountCode } from './DiscountCode';
import { WebshopFieldAnswer } from './WebshopField';
import { AnyCheckoutMethodDecoder, CheckoutMethodType, WebshopDeliveryMethod, WebshopTimeSlot } from './WebshopMetaData';
export class Checkout extends AutoEncoder {
    constructor() {
        super(...arguments);
        this.timeSlot = null;
        this.checkoutMethod = null;
        /**
         * Only needed for delivery
         */
        this.address = null;
        this.customer = Customer.create({});
        this.fieldAnswers = [];
        this.recordAnswers = [];
        this.cart = Cart.create({});
        this.paymentMethod = null;
        this.administrationFee = 0;
        this.discounts = [];
        this.discountCodes = [];
        /**
         * Applied fixed discount (not applicable to a specific cart item)
         */
        this.fixedDiscount = 0;
        /**
         * Applied percentage discount (not applicable to a specific cart item)
         * in pertenthousand
         */
        this.percentageDiscount = 0;
        /**
         * Number of persons we did reserve in webshop time slots (and maybe future other maximums)
         */
        this.reservedPersons = 0;
        /**
         * Whether we reserved order stock in webshop time slots (and maybe future other maximums)
         */
        this.reservedOrder = false;
    }
    get paymentContext() {
        var _a, _b;
        if (((_a = this.checkoutMethod) === null || _a === void 0 ? void 0 : _a.type) == CheckoutMethodType.Takeout) {
            return "takeout";
        }
        if (((_b = this.checkoutMethod) === null || _b === void 0 ? void 0 : _b.type) == CheckoutMethodType.Delivery) {
            return "delivery";
        }
        return null;
    }
    get deliveryPrice() {
        if (!this.checkoutMethod || this.checkoutMethod.type != CheckoutMethodType.Delivery) {
            return 0;
        }
        if (!(this.checkoutMethod instanceof WebshopDeliveryMethod)) {
            // will throw in validation
            return 0;
        }
        if (this.checkoutMethod.price.minimumPrice !== null && this.cart.price >= this.checkoutMethod.price.minimumPrice) {
            return this.checkoutMethod.price.discountPrice;
        }
        return this.checkoutMethod.price.price;
    }
    get appliedPercentageDiscount() {
        return Math.round(this.cart.price * this.percentageDiscount / 10000);
    }
    get totalPrice() {
        // Percentage discount
        // + this.administrationFee;
        return Math.max(0, this.cart.price - this.appliedPercentageDiscount - this.fixedDiscount) + this.deliveryPrice + this.administrationFee;
    }
    get priceBreakown() {
        const all = [
            {
                name: Formatter.percentage(this.percentageDiscount) + ' korting',
                price: -this.appliedPercentageDiscount
            },
            {
                name: 'Korting',
                price: -this.fixedDiscount
            },
            {
                name: 'Leveringskost',
                price: this.deliveryPrice
            },
            {
                name: 'Administratiekost',
                price: this.administrationFee
            },
        ].filter(a => a.price !== 0);
        if (all.length > 0) {
            all.unshift({
                name: 'Subtotaal',
                price: this.cart.priceWithDiscounts
            });
        }
        return [
            ...all,
            {
                name: 'Totaal',
                price: this.totalPrice
            }
        ];
    }
    get totalPriceWithoutAdministrationFee() {
        return this.totalPrice - this.administrationFee;
    }
    validateAnswers(webshop) {
        const newAnswers = [];
        for (const field of webshop.meta.customFields) {
            const answer = this.fieldAnswers.find(a => a.field.id === field.id);
            try {
                if (!answer) {
                    const a = WebshopFieldAnswer.create({ field, answer: "" });
                    a.validate();
                    newAnswers.push(a);
                }
                else {
                    answer.field = field;
                    answer.validate();
                    newAnswers.push(answer);
                }
            }
            catch (e) {
                if (isSimpleError(e) || isSimpleErrors(e)) {
                    e.addNamespace("fieldAnswers." + field.id);
                }
                throw e;
            }
        }
        this.fieldAnswers = newAnswers;
    }
    validateCart(webshop, organizationMeta, asAdmin = false) {
        if (this.cart.items.length == 0) {
            throw new SimpleError({
                code: "cart_empty",
                message: "Cart is empty",
                human: "Jouw winkelmandje is leeg",
                field: "cart"
            });
        }
        const totalItems = this.cart.items.reduce((a, b) => a + b.amount, 0);
        if (totalItems > 1000) {
            throw new SimpleError({
                code: "too_many_items",
                message: "Too many items",
                human: "Je kan maximaal 1000 items tegelijkertijd bestellen",
                field: "cart",
            });
        }
        try {
            this.cart.validate(webshop, asAdmin);
        }
        catch (e) {
            if (isSimpleError(e) || isSimpleErrors(e)) {
                e.addNamespace("cart");
            }
            // also update discounts on errors
            this.updateDiscounts(webshop);
            this.calculatePrices();
            throw e;
        }
        this.updateDiscounts(webshop);
        this.calculatePrices();
        if (!asAdmin && webshop.meta.availableUntil && webshop.meta.availableUntil < new Date()) {
            throw new SimpleError({
                code: "closed",
                message: "Orders are closed",
                human: "Helaas! Je bent te laat. De bestellingen zijn gesloten.",
            });
        }
        if (!asAdmin && webshop.meta.openAt && webshop.meta.openAt > new Date()) {
            throw new SimpleError({
                code: "closed",
                message: "Orders are closed",
                human: "Nog even geduld. Bestellen kan vanaf " + Formatter.dateTime(webshop.meta.openAt) + ".",
            });
        }
        if (!asAdmin && webshop.isClosed()) {
            throw new SimpleError({
                code: "closed",
                message: "Orders are closed",
                human: "De webshop is gesloten. Je kan niet langer bestellen.",
            });
        }
    }
    validateCheckoutMethod(webshop, organizationMeta) {
        if (this.checkoutMethod == null) {
            if (webshop.meta.checkoutMethods.length > 0) {
                throw new SimpleError({
                    code: "missing_checkout_method",
                    message: "Checkout method is required",
                    human: "Er zijn enkele instellingen gewijzigd terwijl je aan het bestellen was. Herlaad de pagina en probeer opnieuw.",
                    field: "checkoutMethod"
                });
            }
            return;
        }
        const current = this.checkoutMethod;
        const checkoutMethod = webshop.meta.checkoutMethods.find(m => m.id == current.id);
        if (!checkoutMethod) {
            if (webshop.meta.checkoutMethods.length === 0) {
                this.checkoutMethod = null;
                return;
            }
            throw new SimpleError({
                code: "invalid_checkout_method",
                message: "Checkout method is invalid",
                human: "Er zijn enkele instellingen gewijzigd terwijl je aan het bestellen was. Herlaad de pagina en probeer opnieuw.",
                field: "checkoutMethod"
            });
        }
        this.checkoutMethod = checkoutMethod;
    }
    validateDeliveryAddress(webshop, organizationMeta) {
        if (!this.checkoutMethod || this.checkoutMethod.type != CheckoutMethodType.Delivery) {
            this.address = null;
            return;
        }
        if (!(this.checkoutMethod instanceof WebshopDeliveryMethod)) {
            throw new SimpleError({
                code: "invalid_data_type",
                message: "Invalid data type",
                human: "Er ontbreekt data. Probeer het opnieuw of neem contact op met de webshop eigenaar om dit te melden.",
                field: "address"
            });
        }
        if (!this.address) {
            throw new SimpleError({
                code: "missing_address",
                message: "Checkout address is invalid",
                human: "Een leveringsadres is noodzakelijk, herlaad de pagina opnieuw en vul een leveringsadres in.",
                field: "address"
            });
        }
        // Check country
        if (this.checkoutMethod.countries.includes(this.address.country)) {
            return;
        }
        if (this.checkoutMethod.provinces.map(p => p.id).includes(this.address.provinceId)) {
            return;
        }
        if (this.checkoutMethod.cities.map(c => c.id).includes(this.address.cityId)) {
            return;
        }
        if (this.address.parentCityId && this.checkoutMethod.cities.map(c => c.id).includes(this.address.parentCityId)) {
            return;
        }
        throw new SimpleError({
            code: "region_not_supported",
            message: "Region not supported",
            human: "We leveren jammer genoeg niet op dit adres.",
            field: "address"
        });
    }
    validateTimeSlot(webshop, organizationMeta) {
        if (!this.checkoutMethod || this.checkoutMethod.timeSlots.timeSlots.length == 0) {
            this.timeSlot = null;
            return;
        }
        if (!this.timeSlot) {
            throw new SimpleError({
                code: "missing_timeslot",
                message: "Checkout timeslot is missings",
                human: "Kies een tijdstip, herlaad de pagina opnieuw en maak een keuze.",
                field: "timeSlot"
            });
        }
        const current = this.timeSlot;
        const timeSlot = this.checkoutMethod.timeSlots.timeSlots.find(s => s.id == current.id);
        const availableTimeslots = this.checkoutMethod.timeSlots.timeSlots.length;
        if (!timeSlot) {
            throw new SimpleError({
                code: "invalid_timeslot",
                message: "Checkout timeslot is invalid",
                human: "Het afhaaltijdstip dat je had gekozen werd gewijzigd terwijl je aan het bestellen was, kies een nieuw tijdstip.",
                field: "timeSlot"
            });
        }
        // Check maximum
        if (!this.reservedOrder && timeSlot.remainingOrders === 0) {
            throw new SimpleError({
                code: "timeslot_full",
                message: "Timeslot has reached maximum orders",
                human: (availableTimeslots != 1 ? "Het gekozen tijdstip is helaas volzet. Kies een ander tijdstip indien mogelijk." : "Het evenement is helaas volzet. We aanvaarden geen verdere bestellingen."),
                field: "timeSlot"
            });
        }
        // Check maximum
        if (timeSlot.remainingPersons !== null && this.cart.persons - this.reservedPersons > timeSlot.remainingPersons) {
            const remainingPersons = timeSlot.remainingPersons;
            if (remainingPersons === 0) {
                throw new SimpleError({
                    code: "timeslot_full",
                    message: "Timeslot has reached maximum orders",
                    human: (availableTimeslots != 1 ? "Het gekozen tijdstip is helaas volzet. Kies een ander tijdstip indien mogelijk." : "Het evenement is helaas volzet. We aanvaarden geen verdere bestellingen."),
                    field: "timeSlot"
                });
            }
            throw new SimpleError({
                code: "timeslot_full",
                message: "Timeslot has reached maximum persons",
                human: "Er " + (remainingPersons != 1 ? "zijn" : "is") + " nog maar " + remainingPersons + " " + (remainingPersons != 1 ? "plaatsen" : "plaats") + " vrij " + (availableTimeslots != 1 ? "op het gekozen tijdstip" : "voor dit evenement") + ". Jouw mandje is voor " + this.cart.persons + " " + (this.cart.persons != 1 ? "personen" : "persoon") + (availableTimeslots != 1 ? ". Kies een ander tijdstip indien mogelijk." : ""),
                field: "timeSlot"
            });
        }
        this.timeSlot = timeSlot;
    }
    validateCustomer(webshop, organizationMeta, i18n, asAdmin = false, user = null) {
        if (user) {
            if (user.firstName) {
                this.customer.firstName = user.firstName;
            }
            if (user.lastName) {
                this.customer.lastName = user.lastName;
            }
            this.customer.email = user.email;
        }
        if (this.customer.firstName.length < 2) {
            throw new SimpleError({
                code: "invalid_first_name",
                message: "Invalid first name",
                human: "De voornaam die je hebt opgegeven is ongeldig, corrigeer het voor je verder gaat.",
                field: "customer.firstName"
            });
        }
        if (this.customer.lastName.length < 2) {
            throw new SimpleError({
                code: "invalid_last_name",
                message: "Invalid last name",
                human: "De achternaam die je hebt opgegeven is ongeldig, corrigeer het voor je verder gaat.",
                field: "customer.lastName"
            });
        }
        if (webshop.meta.phoneEnabled) {
            if (this.customer.phone.length < 6 && !asAdmin) {
                throw new SimpleError({
                    code: "invalid_phone",
                    message: "Invalid phone",
                    human: i18n.t('shared.inputs.mobile.invalidMessage').toString(),
                    field: "customer.phone"
                });
            }
        }
        else {
            this.customer.phone = "";
        }
        const regex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
        if (!regex.test(this.customer.email)) {
            throw new SimpleError({
                code: "invalid_email",
                message: "Invalid email",
                human: "Het e-mailadres dat je hebt opgegeven is ongeldig, corrigeer het voor je verder gaat.",
                field: "customer.email"
            });
        }
        this.validateAnswers(webshop);
    }
    validatePayment(webshop, organizationMeta) {
        if (!this.paymentMethod) {
            throw new SimpleError({
                code: "missing_payment_method",
                message: "Missing payment method",
                human: "Kies een betaalmethode",
                field: "paymentMethod"
            });
        }
        if (!webshop.meta.paymentMethods.includes(this.paymentMethod)) {
            throw new SimpleError({
                code: "invalid_payment_method",
                message: "Invalid payment method",
                human: "Deze betaalmethode is niet meer beschikbaar. Herlaad eventueel de pagina en probeer opnieuw.",
                field: "paymentMethod"
            });
        }
    }
    validateRecordAnswersFor(webshop, category) {
        RecordCategory.validate([category], this.recordAnswers, this, Checkout.getFilterDefinitions(webshop, webshop.meta.recordCategories), true);
    }
    validateRecordAnswers(webshop) {
        const answers = RecordCategory.validate(webshop.meta.recordCategories, this.recordAnswers, this, Checkout.getFilterDefinitions(webshop, webshop.meta.recordCategories), true);
        this.recordAnswers = answers;
    }
    updateAdministrationFee(webshop) {
        this.administrationFee = webshop.meta.paymentConfiguration.administrationFee.calculate(this.totalPriceWithoutAdministrationFee);
    }
    updateDiscounts(webshop) {
        this.discounts = webshop.meta.defaultDiscounts.slice();
        this.discounts.push(...this.discountCodes.flatMap(c => c.discounts));
    }
    calculatePrices() {
        // Group discounts by discounts that can get applied together with other discounts
        // for now: everything can get combined
        const discountOrders = [this.discounts];
        for (const discounts of discountOrders) {
            this.fixedDiscount = 0;
            this.percentageDiscount = 0;
            for (const item of this.cart.items) {
                item.discounts = [];
                // Reset all discounts on this item
                item.calculatePrices(this.cart);
            }
            const trackers = [];
            for (const discount of discounts) {
                trackers.push(...discount.applyToCheckout(this));
            }
            // Loop trackers and apply the one with the current highest potential
            while (true) {
                let bestPotential = null;
                for (const tracker of trackers) {
                    const potential = tracker.getPotentialDiscount();
                    if (potential !== 0 && (bestPotential === null || potential > bestPotential.potential)) {
                        bestPotential = { tracker, potential };
                    }
                }
                if (bestPotential) {
                    bestPotential.tracker.apply();
                }
                else {
                    break;
                }
            }
        }
    }
    update(webshop) {
        this.updateDiscounts(webshop);
        this.calculatePrices();
        this.updateAdministrationFee(webshop);
    }
    validate(webshop, organizationMeta, i18n, asAdmin = false, user = null) {
        try {
            this.validateCart(webshop, organizationMeta, asAdmin);
            this.validateCheckoutMethod(webshop, organizationMeta);
            this.validateDeliveryAddress(webshop, organizationMeta);
            this.validateTimeSlot(webshop, organizationMeta);
            this.validateCustomer(webshop, organizationMeta, i18n, asAdmin, user);
            this.validateRecordAnswers(webshop);
            if (this.totalPrice !== 0 && !asAdmin) {
                this.validatePayment(webshop, organizationMeta);
            }
            else if (this.totalPrice === 0) {
                this.paymentMethod = PaymentMethod.Unknown;
            }
        }
        finally {
            this.update(webshop);
        }
    }
    /**
     * Convenience mapper
     */
    get deliveryMethod() {
        if (!this.checkoutMethod || this.checkoutMethod.type != CheckoutMethodType.Delivery) {
            return null;
        }
        if (!(this.checkoutMethod instanceof WebshopDeliveryMethod)) {
            // will throw in validation
            return null;
        }
        return this.checkoutMethod;
    }
    static getFilterDefinitions(webshop, categories) {
        var _a, _b;
        const filters = RecordCategory.getRecordCategoryDefinitions(categories, (checkout) => {
            return checkout.recordAnswers;
        });
        if (webshop.meta.checkoutMethods.length) {
            filters.push(new ChoicesFilterDefinition({
                id: "order_checkoutMethod",
                name: "Afhaal/leveringsmethode",
                choices: ((_a = webshop.meta.checkoutMethods) !== null && _a !== void 0 ? _a : []).flatMap(method => {
                    // TODO: also add checkout methods that are not valid anymore from existing orders
                    const choices = [];
                    if (method.timeSlots.timeSlots.length == 0) {
                        choices.push(new ChoicesFilterChoice(method.id, method.type + ": " + method.name));
                    }
                    for (const time of method.timeSlots.timeSlots) {
                        choices.push(new ChoicesFilterChoice(method.id + "-" + time.id, method.type + ": " + method.name, time.toString()));
                    }
                    return choices;
                }),
                defaultMode: ChoicesFilterMode.Or,
                getValue: (checkout) => {
                    const ids = [];
                    if (checkout.checkoutMethod) {
                        ids.push(checkout.checkoutMethod.id);
                        if (checkout.timeSlot) {
                            ids.push(checkout.checkoutMethod.id + "-" + checkout.timeSlot.id);
                        }
                    }
                    return ids;
                }
            }));
        }
        filters.push(new ChoicesFilterDefinition({
            id: "order_products",
            name: "Bestelde artikels",
            choices: ((_b = webshop.products) !== null && _b !== void 0 ? _b : []).map(product => {
                return new ChoicesFilterChoice(product.id, product.name + (product.dateRange ? " (" + product.dateRange.toString() + ")" : ""));
            }),
            defaultMode: ChoicesFilterMode.Or,
            getValue: (checkout) => {
                return checkout.cart.items.flatMap(i => i.product.id);
            }
        }));
        const priceChoices = [];
        for (const product of webshop.products) {
            if (product.prices.length > 1) {
                for (const price of product.prices) {
                    priceChoices.push(new ChoicesFilterChoice(product.id + ':' + price.id, product.name + ": " + price.name));
                }
            }
        }
        if (priceChoices.length > 0) {
            filters.push(new ChoicesFilterDefinition({
                id: "order_product_prices",
                name: "Bestelde prijskeuzes",
                choices: priceChoices,
                defaultMode: ChoicesFilterMode.Or,
                getValue: (checkout) => {
                    return checkout.cart.items.flatMap(i => i.product.id + ':' + i.productPrice.id);
                }
            }));
        }
        return filters;
    }
}
__decorate([
    field({ decoder: WebshopTimeSlot, nullable: true })
], Checkout.prototype, "timeSlot", void 0);
__decorate([
    field({ decoder: AnyCheckoutMethodDecoder, nullable: true })
], Checkout.prototype, "checkoutMethod", void 0);
__decorate([
    field({ decoder: ValidatedAddress, nullable: true })
], Checkout.prototype, "address", void 0);
__decorate([
    field({ decoder: Customer, version: 40 })
], Checkout.prototype, "customer", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(WebshopFieldAnswer), version: 94 })
], Checkout.prototype, "fieldAnswers", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(RecordAnswerDecoder), optional: true })
], Checkout.prototype, "recordAnswers", void 0);
__decorate([
    field({ decoder: Cart })
], Checkout.prototype, "cart", void 0);
__decorate([
    field({ decoder: new EnumDecoder(PaymentMethod), nullable: true })
], Checkout.prototype, "paymentMethod", void 0);
__decorate([
    field({ decoder: IntegerDecoder, version: 207 })
], Checkout.prototype, "administrationFee", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(Discount), version: 235 })
], Checkout.prototype, "discounts", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(DiscountCode), version: 239 })
], Checkout.prototype, "discountCodes", void 0);
__decorate([
    field({ decoder: IntegerDecoder, version: 235 })
], Checkout.prototype, "fixedDiscount", void 0);
__decorate([
    field({ decoder: IntegerDecoder, version: 235 })
], Checkout.prototype, "percentageDiscount", void 0);
__decorate([
    field({ decoder: NumberDecoder, version: 143 })
], Checkout.prototype, "reservedPersons", void 0);
__decorate([
    field({ decoder: BooleanDecoder, version: 143 })
], Checkout.prototype, "reservedOrder", void 0);
//# sourceMappingURL=Checkout.js.map